import { SieveFilter, SieveSort } from './sieve-filters-and-sorts';

export interface ISieveConf {
    page?: number;
    pageSize?: number;
    filters?: ISieveFilterObject[];
    sorts?: ISieveSortsObject[];
}
export interface ISieveFilterObject {
    key: SieveFilter;
    operator: SieveOperator;
    value: string | string[] | number | number[] | boolean;
}
export interface ISieveSortsObject {
    key: SieveSort;
    order: SieveSortOrder;
}
export enum SieveSortOrder {
    Asc = '',
    Desc = '-'
}
export enum SieveOperator {
    Equal = '==',
    Contains = '@=',
    GoE = '>=',
    LoE = '<=',
    NotEqual = '!='
}

//TODO: escaping special chrs
export class SieveString {
    public static readonly DEAFULT_PAGE = 1;
    public static readonly DEAFULT_PAGE_SIZE = 20;
    private readonly _startString = '?';
    private readonly _separatorString = '&';
    private readonly _internalSeparatorStringOR = '|';
    private readonly _AND = ',';
    private readonly _pagePref = 'page=';
    private readonly _pageSizePref = 'pageSize=';
    private readonly _sortPref = 'sorts=';
    private readonly _filtersPref = 'filters=';
    private _page: number = null;
    private _pageSize: number = null;
    private _filters = '';
    private _sorts = '';
    private withQuerstionMark = true;

    constructor(sieveConf?: ISieveConf, defaults: boolean = false) {
        if (defaults) {
            this.resetDefault();
        } else {
            this.cleanAll();
        }
        if (sieveConf) {
            this.setPage(sieveConf.page);
            this.setPageSize(sieveConf.pageSize);
            this.addFilters(sieveConf.filters);
            this.addSorts(sieveConf.sorts);
        }
    }

    resetDefault() {
        this.setPage(SieveString.DEAFULT_PAGE);
        this.setPageSize(SieveString.DEAFULT_PAGE_SIZE);
        this._filters = '';
        this._sorts = '';
    }
    cleanAll() {
        this._page = null;
        this._pageSize = null;
        this._filters = '';
        this._sorts = '';
    }

    cleanFilters() {
        this._filters = '';
    }
    cleanSorts() {
        this._sorts = '';
    }

    setPage(page: number) {
        if (page) {
            this._page = page;
        }
    }
    setPageSize(pageSize: number) {
        if (pageSize) {
            this._pageSize = pageSize;
        }
    }

    //SORTS
    private _addSort(sort: string) {
        if (sort != null && sort.length > 0) {
            if (this._sorts.length > 0) {
                if (!this._sorts.includes(sort)) {
                    this._sorts = this._sorts + this._AND + sort;
                }
            } else {
                this._sorts = sort;
            }
        }
    }

    private prepareSort(sort: ISieveSortsObject | string): string {
        let _sort = '';
        if (sort != null) {
            if (typeof sort === 'string') {
                _sort = sort;
            } else {
                //sort.value can be null (see SieveSortOrder.Asc)
                if (sort.key != null && sort.order != null) {
                    _sort = sort.order + sort.key;
                }
            }
        }
        return _sort;
    }

    addSorts(sorts: ISieveSortsObject[] | string[]) {
        if (sorts != null) {
            for (const sort of sorts) {
                this._addSort(this.prepareSort(sort));
            }
        }
    }

    //FILTERS
    private _addFilter(filter: string) {
        if (filter != null && filter.length > 0) {
            if (this._filters.length > 0) {
                if (!this._filters.includes(filter)) {
                    this._filters = this._filters + this._AND + filter;
                }
            } else {
                this._filters = filter;
            }
        }
    }

    private prepareFilter(filter: ISieveFilterObject | string): string {
        let _filter = '';
        if (filter != null) {
            if (typeof filter === 'string') {
                _filter = filter;
            } else {
                if (filter.key != null && filter.operator != null && filter.value != null) {
                    if (filter.key instanceof Array && filter.value instanceof Array) {
                        return null;
                    }
                    //set _key
                    const _key = filter.key;
                    //set _operator
                    const _operator = filter.operator;
                    // set _value
                    let _value: string;
                    if (typeof filter.value === 'string') {
                        _value = filter.value;
                    } else if (typeof filter.value === 'number') {
                        _value = filter.value.toString();
                    } else if (typeof filter.value === 'boolean') {
                            _value = filter.value.toString();
                    } else {
                        if (filter.value.length === 0) {
                            return '';
                        }
                        const _firstElement = filter.value[0];
                        if (typeof _firstElement === 'string') {
                            _value = filter.value.join(this._internalSeparatorStringOR);
                        } else if (typeof _firstElement === 'number') {
                            const strArr: string[] = (<number[]>filter.value).map(n =>
                                n.toString()
                            );
                            _value = strArr.join(this._internalSeparatorStringOR);
                        } else {
                            return '';
                        }
                    }
                    //set _filter
                    _filter = _key + _operator + _value;
                }
            }
        }
        return _filter;
    }

    addFilters(filters: ISieveFilterObject[] | string[]) {
        if (filters) {
            for (const filter of filters) {
                this._addFilter(this.prepareFilter(filter));
            }
        }
    }

    private _wrapCompositeKeyWithRoundBrakets(str: string) {
        return '(' + str + ')';
    }

    _pageString(): string | null {
        if (typeof this._page !== 'undefined' && this._page !== null) {
            return this._pagePref + this._page.toString();
        }
        return null;
    }
    _pageSizeString(): string | null {
        if (typeof this._pageSize !== 'undefined' && this._pageSize !== null) {
            return this._pageSizePref + this._pageSize.toString();
        }
        return null;
    }
    _filterString(): string | null {
        if (this._filters.length > 0) {
            return this._filtersPref + this._filters;
        }
        return null;
    }
    _sortString(): string | null {
        if (this._sorts.length > 0) {
            return this._sortPref + this._sorts;
        }
        return null;
    }

    withQuestionMark(bool: boolean = true) {
        this.withQuerstionMark = bool;
        return this;
    }

    toString() {
        const _pageString = this._pageString();
        const _pageSizeString = this._pageSizeString();
        const _sortString = this._sortString();
        const _filterString = this._filterString();
        let _sieveString = '';
        for (const str of [_pageString, _pageSizeString, _sortString, _filterString]) {
            if (typeof str !== 'undefined' && str !== null && str.length > 0) {
                if (_sieveString.length > 0) {
                    _sieveString = _sieveString + this._separatorString + str;
                } else {
                    _sieveString = str;
                }
            }
        }
        if (_sieveString.length > 0 && this.withQuerstionMark) {
            _sieveString = this._startString + _sieveString;
        }
        return _sieveString;
    }
}



// WEBPACK FOOTER //
// ./src/app/helpers/sieve/sieveString.class.ts